import CopyLinkButton from '@/components/Discord/CopyLinkButton'
import { useState } from 'react'
import styles from '@/components/Discord/DiscordDocument.module.css'
import DiscordDocument from '@/components/Discord/DiscordDocument'

function DiscordReportingAbusiveBehaviorPage() {
  const [language, setLanguage] = useState<'en' | 'cs'>('cs')

  return (
    <DiscordDocument
      language={language}
      onLanguageChange={setLanguage}
      heading={
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            {language === 'cs'
              ? 'Nahlašování obsahu a chování na Discordu'
              : 'Reporting Content or Behavior to Discord'}
          </h1>
        </div>
      }
      notice={
        <>
          <div className={styles.disclaimer}>
            <div className={styles.disclaimerCard}>
              <h3>{language === 'cs' ? 'Důležité upozornění' : 'Important Notice'}</h3>
              <p>
                {language === 'cs'
                  ? 'Toto je komunitní překlad článku od Allcom komunity. Nejsme spojeni s Discord Inc.'
                  : 'This is a community translation by Allcom community. We are not affiliated with Discord Inc.'}
              </p>
              <p>
                {language === 'cs'
                  ? 'V případě nesrovnalostí platí vždy '
                  : 'In case of discrepancies, the '}
                <a
                  href="https://discord.com/safety/360044103651-reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {language === 'cs' ? 'originální anglická verze' : 'original English version'}
                </a>
                {language === 'cs' ? '.' : ' always applies.'}
              </p>
            </div>

            <div className={styles.disclaimerCard}>
              <h3>{language === 'cs' ? 'Právní informace' : 'Legal Information'}</h3>
              <p>
                {language === 'cs'
                  ? 'Discord je ochranná známka společnosti Discord Inc. Všechna práva vyhrazena. Používání této značky není sponzorováno ani schváleno společností Discord Inc.'
                  : 'Discord is a trademark of Discord Inc. All rights reserved. Use of this trademark is not sponsored or approved by Discord Inc.'}
              </p>
            </div>
          </div>
        </>
      }
    >
      {language === 'cs' && (
        <details id="quick-summary" className={styles.quickSummary}>
          <summary>
            <h2>Rychlé shrnutí</h2>
          </summary>
          <CopyLinkButton
            sectionId={'quick-summary'}
            language={language}
            className={styles.copyBtn}
          />

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h3>Jak nahlásit</h3>
              <ul>
                <li>Zprávu nahlásíte dlouhým podržením (mobil) nebo pravým klikem (PC)</li>
                <li>Uživatelský profil nahlásíte přes menu tří teček</li>
                <li>Vždy vyberte správnou kategorii porušení</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Priorita vyřízení</h3>
              <ul>
                <li>Vysoká závažnost: cílem je kontrola do 24–48 hodin</li>
                <li>Nižší závažnost: cílem je kontrola do týdne</li>
                <li>Komplexní případy: dny až týdny</li>
                <li>Jde o cílové doby, které se mohou změnit</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Vaše ochrana</h3>
              <ul>
                <li>Identita se nahlášenému účtu běžně nesděluje; existují zákonné výjimky</li>
                <li>Falešná hlášení jsou proti pravidlům</li>
                <li>Discord vás informuje o výsledku</li>
              </ul>
            </div>
          </div>
          <p className={styles.summaryNote}>
            Toto je pouze stručné shrnutí. Pro úplné znění čtěte celý dokument níže.
          </p>
        </details>
      )}

      {/* Article Body */}
      <div className={styles.articleBody}>
        <section className={styles.articleSection}>
          <p>
            {language === 'cs'
              ? 'Pokud narazíte na porušení našich '
              : 'If you encounter a violation of our '}
            <a href="/discord/terms">
              {language === 'cs' ? 'Podmínek služby' : 'Terms of Service'}
            </a>
            {language === 'cs' ? ' nebo ' : ' or '}
            <a href="/discord/guidelines">
              {language === 'cs' ? 'Pokynů komunity' : 'Community Guidelines'}
            </a>
            {language === 'cs'
              ? ', žádáme vás, abyste nám toto chování nahlásili.'
              : ', we ask that you report this behavior to us.'}
          </p>
        </section>

        <section id="reporting-applications" className={styles.articleSection}>
          <CopyLinkButton
            sectionId="reporting-applications"
            language={language}
            className={styles.copyBtn}
          />
          <h2>{language === 'cs' ? 'Nahlášení aplikace' : 'Reporting an application'}</h2>
          <p>
            {language === 'cs'
              ? 'Pro chování nebo profil aplikace použijte samostatný '
              : 'For an app’s behaviour or profile, follow the separate '}
            <a href="https://discord.com/safety/reporting-applications-to-discord">
              {language === 'cs' ? 'návod k nahlašování aplikací' : 'application reporting guide'}
            </a>
            .
          </p>
        </section>
        <section id="reporting-message" className={styles.articleSection}>
          <CopyLinkButton
            sectionId={'reporting-message'}
            language={language}
            className={styles.copyBtn}
          />
          <h2>{language === 'cs' ? 'Nahlášení zprávy' : 'Reporting a Message'}</h2>

          <ol>
            <li>
              {language === 'cs'
                ? 'Vyberte zprávu, kterou chcete nahlásit. Na mobilu podržte zprávu, na počítači klikněte pravým tlačítkem.'
                : 'Select the Message you wish to report. On mobile, hold down on the Message, and on desktop, "right-click."'}
            </li>
            <li>
              {language === 'cs' ? 'Vyberte "' : 'Select "'}
              <strong>{language === 'cs' ? 'Nahlásit zprávu' : 'Report Message'}</strong>
              {'"'}
            </li>
            <li>
              {language === 'cs'
                ? 'Vyberte typ porušení našich pravidel, které vidíte:'
                : "Select the type of violation of our policies that you're seeing:"}
            </li>
          </ol>

          <div className={styles.imageContainer}>
            <img
              src="https://cdn.prod.website-files.com/625fe439fb70a9d901e138ab/641c9b192c20c42ed1052f55_Report%20Message%20Prompt-3.png"
              alt={language === 'cs' ? 'Prompt pro nahlášení zprávy' : 'Report Message Prompt'}
              loading="lazy"
            />
          </div>

          <ol start={4}>
            <li>
              {language === 'cs'
                ? 'Další obrazovka vám umožní přesněji identifikovat konkrétní porušení pravidel. Vždy se můžete vrátit zpět a změnit svou první odpověď, abyste mohli vybrat nejrelevantnější kategorii.'
                : "The next screen will allow you to further identify the specific policy violation that's occurring. You can always click back and change your first answer, so you can select the most relevant category."}
            </li>
          </ol>

          <p>
            {language === 'cs'
              ? 'Pokud k porušení došlo na serveru, můžete se také obrátit na moderátory serveru, kteří mohou okamžitě reagovat a pomoci vyřešit vaše obavy. Kromě toho nezapomeňte, že máte vždy možnost '
              : "If the violation happened in a server, you can also reach out to the server's moderators, who may be able to respond immediately and help resolve your concerns. In addition, please remember that you always have the ability to "}
            <a href="https://support.discord.com/hc/en-us/articles/28084948873623-How-to-Ignore-Users-on-Discord">
              {language === 'cs' ? 'ignorovat' : 'ignore'}
            </a>
            {language === 'cs' ? ' nebo ' : ' or '}
            <a href="https://discord.com/safety/360043857751-Four-steps-to-a-super-safe-account">
              {language === 'cs' ? 'zablokovat jakékoliv uživatele' : 'block any users'}
            </a>
            {language === 'cs'
              ? ', se kterými chcete omezit interakci nebo s nimi již nechcete komunikovat.'
              : " that you want to limit interaction with or don't want to interact with anymore."}
          </p>

          <p className={styles.warning}>
            <strong>
              {language === 'cs'
                ? 'Upozornění: Je proti našim pravidlům uvádět v omyl týmy podpory Discordu'
                : "Please note: It is against our policies to mislead Discord's support teams"}
            </strong>
            {language === 'cs'
              ? '. Nepodávejte falešná nebo zlomyslná hlášení našim týmům Trust & Safety nebo jiným týmům zákaznické podpory, neposílejte více hlášení o stejném problému a nežádejte skupinu uživatelů, aby nahlásili stejný obsah nebo problém.'
              : '. Do not make false or malicious reports to our Trust & Safety or other customer support teams, send multiple reports about the same issue, or ask a group of users to report the same content or issue.'}
          </p>
        </section>

        <section id="violence-self-harm" className={styles.articleSection}>
          <CopyLinkButton
            sectionId={'violence-self-harm'}
            language={language}
            className={styles.copyBtn}
          />
          <h2>
            {language === 'cs'
              ? 'Co dělat, pokud obdržíte násilnou hrozbu nebo je někdo v ohrožení sebepoškozením'
              : 'What to do if you receive a violent threat, or someone is at risk of Self-harm'}
          </h2>

          <p className={styles.emergency}>
            <strong>
              {language === 'cs'
                ? 'Pokud byla učiněna věrohodná hrozba násilí a vy nebo někdo jiný jste v bezprostředním nebezpečí, nebo pokud někdo uvažuje o sebepoškození a je v bezprostředním nebezpečí, kontaktujte prosím místní orgány činné v trestním řízení.'
                : 'If a credible threat of violence has been made and you or someone else are in immediate danger, or if someone is considering self-harm and is in immediate danger, please contact your local law enforcement agency.'}
            </strong>
          </p>

          <p>
            {language === 'cs'
              ? 'Pokud se nacházíte ve Spojených státech, můžete kontaktovat '
              : 'Additionally, if you are in the United States, you can contact '}
            <a href="https://www.crisistextline.org/">Crisis Text Line</a>
            {language === 'cs'
              ? ', abyste si promluvili s dobrovolným krizovým poradcem, který vám nebo vašemu příteli pomůže zvládnout jakoukoli krizi duševního zdraví zasláním SMS s textem DISCORD na číslo 741741. Více informací o partnerství Discordu s Crisis Text Line najdete '
              : " to speak with a volunteer crisis counselor to help you or a friend through any mental health crisis by texting DISCORD to 741741. You can learn more about Discord's partnership with Crisis Text line "}
            <a href="https://support.discord.com/hc/en-us/articles/7925648993943-Crisis-Text-Line">
              {language === 'cs' ? 'zde' : 'here'}
            </a>
            {'.'}
          </p>

          <p>
            {language === 'cs'
              ? 'Pokud se nacházíte v jiném regionu, můžete kontaktovat '
              : 'If you are located in another region, you can contact '}
            <a href="https://discord.findahelpline.com/">ThroughLine Care</a>
            {language === 'cs'
              ? ', kde najdete bezplatnou a důvěrnou podporu ve vaší zemi prostřednictvím chatu, SMS nebo telefonu.'
              : ' to find free, confidential support in your country via chat, text, or phone.'}
          </p>

          <p>
            <strong>
              {language === 'cs'
                ? 'Více zdrojů o duševním zdraví najdete '
                : 'You can find more resources about mental health '}
            </strong>
            <a href="https://discord.com/safety/360044103771-mental-health-on-discord">
              <strong>{language === 'cs' ? 'zde' : 'here'}</strong>
            </a>
            <strong>.</strong>
          </p>
        </section>

        <section id="reporting-user-profile" className={styles.articleSection}>
          <CopyLinkButton
            sectionId={'reporting-user-profile'}
            language={language}
            className={styles.copyBtn}
          />
          <h2>
            {language === 'cs' ? 'Nahlášení uživatelského profilu' : 'Reporting a User Profile'}
          </h2>

          <ol>
            <li>
              {language === 'cs'
                ? 'Vyberte uživatelský profil, který chcete nahlásit, kliknutím na menu tří teček'
                : 'Select the User Profile you wish to report by clicking on the three dot menu'}
            </li>
            <li>
              {language === 'cs' ? 'Vyberte "' : 'Select "'}
              <strong>
                {language === 'cs' ? 'Nahlásit uživatelský profil' : 'Report User Profile'}
              </strong>
              {'"'}
            </li>
            <li>
              {language === 'cs'
                ? 'Vyberte konkrétní prvky profilu, které hlásíte - můžete nahlásit více aspektů profilu najednou'
                : 'Select the specific elements of the profile you are reporting - you can report multiple aspects of a profile at once'}
            </li>
          </ol>

          <div className={styles.imageContainer}>
            <img
              src="https://cdn.prod.website-files.com/625fe439fb70a9d901e138ab/651c76e94e729106e2024f70_reporting-elements-discord.png"
              alt={
                language === 'cs' ? 'Prvky hlášení na Discordu' : 'Reporting elements on Discord'
              }
              loading="lazy"
            />
          </div>

          <ol start={4}>
            <li>
              {language === 'cs'
                ? 'Vyberte typ porušení pravidel, které vidíte'
                : "Select the type of policy violation you're seeing"}
            </li>
          </ol>

          <div className={styles.imageContainer}>
            <img
              src="https://cdn.prod.website-files.com/625fe439fb70a9d901e138ab/651c76ff296c2c8430535875_select-report-type-discord.png"
              alt={
                language === 'cs'
                  ? 'Výběr typu hlášení na Discordu'
                  : 'Select report type on Discord'
              }
              loading="lazy"
            />
          </div>

          <ol start={5}>
            <li>
              {language === 'cs'
                ? 'Další obrazovka vám umožní dále specifikovat konkrétní porušení pravidel. Vždy se můžete vrátit zpět a změnit svou první odpověď, abyste mohli vybrat nejrelevantnější kategorii.'
                : "The next screen will allow you to further specify the specific policy violation that's occurring. You can always click back and change your first answer, so you can select the most relevant category."}
            </li>
          </ol>
        </section>

        <section id="eu-dsa-reports" className={styles.articleSection}>
          <CopyLinkButton
            sectionId={'eu-dsa-reports'}
            language={language}
            className={styles.copyBtn}
          />
          <h2>
            {language === 'cs'
              ? 'Hlášení podle zákona EU o digitálních službách'
              : 'Reports Under the EU Digital Services Act'}
          </h2>

          <p>
            {language === 'cs'
              ? 'Uživatelé EU mohou nahlásit nelegální obsah podle zákona EU o digitálních službách kliknutím '
              : 'EU users can report illegal content under the EU Digital Services Act by clicking '}
            <a href="https://discord.com/report">{language === 'cs' ? 'zde' : 'here'}</a>
            {language === 'cs'
              ? '. Vládní subjekty EU hlásící nelegální obsah by měly postupovat podle procesu uvedeného '
              : '. EU government entities reporting illegal content should follow the process outlined '}
            <a href="https://discord.com/safety/360044157931-working-with-law-enforcement">
              {language === 'cs' ? 'zde' : 'here'}
            </a>
            {'.'}
          </p>

          <p>
            {language === 'cs'
              ? 'Uživatelé EU budou muset projít procesem ověření a postupovat podle pokynů k poskytnutí podrobností a popisů pro své hlášení.'
              : 'EU users will be required to go through a verification process and follow the prompts to provide details and descriptions for their report.'}
          </p>

          <p>
            {language === 'cs'
              ? 'Při nahlašování zprávy podle zákona EU o digitálních službách je vyžadována URL zprávy. Zde je návod, jak najít URL zprávy pro desktopovou a mobilní aplikaci.'
              : "When reporting a message under the EU Digital Services Act, a message URL is required. Here's how to find the message URL for the desktop and mobile apps."}
          </p>

          <h3>{language === 'cs' ? 'Desktopová aplikace' : 'Desktop App'}</h3>
          <ol>
            <li>
              {language === 'cs'
                ? 'Přejděte na zprávu, kterou chcete nahlásit.'
                : 'Navigate to the message that you would like to report.'}
            </li>
            <li>
              {language === 'cs'
                ? 'Klikněte pravým tlačítkem na zprávu nebo stiskněte ikonu tří teček při najetí myší na zprávu.'
                : 'Right-click on the message or press on the ellipses icon when hovering over the message.'}
            </li>
          </ol>

          <div className={styles.imageContainer}>
            <img
              src="https://cdn.prod.website-files.com/625fe439fb70a9d901e138ab/65cfa5e2d9bfb8e1cd354933_UmWkCaISN1Jq-ECrsobmojP1EO98H7O9Rqxb5F_RfglLELtmf4Bq18b4bTYsqrtWhiutu0-2LEk2ZKtbIWzxL2wyo2R84vkV2C4K9aIi-qAOIzk9uo6IbJ_C33mNKQGt484XoQDO09ZmTj2gLkDFOI8.png"
              alt={language === 'cs' ? 'Kontextové menu zprávy' : 'Message context menu'}
              loading="lazy"
            />
          </div>

          <ol start={3}>
            <li>
              {language === 'cs' ? 'Vyberte ' : 'Select '}
              <strong>
                {language === 'cs' ? 'Kopírovat odkaz na zprávu' : 'Copy Message Link'}
              </strong>
              {'.'}
            </li>
          </ol>

          <div className={styles.imageContainer}>
            <img
              src="https://cdn.prod.website-files.com/625fe439fb70a9d901e138ab/65cfa5e27bc9b54b99d2f80a__6UYWRc--mn2nhL8UIZmMFscWDCCgwle3n9D8JrUJrRILvmowhjhtAFIPgL9rxh6rESwqbsZeMyeDLtCXwsfxdpLsKZusUh0qs2CguBjFmORRxgqq7QccgyYlz-dJ2eimJR-oXDPgQaA2Uy14hB2XTg.png"
              alt={
                language === 'cs' ? 'Možnost kopírovat odkaz na zprávu' : 'Copy message link option'
              }
              loading="lazy"
            />
          </div>

          <ol start={4}>
            <li>
              {language === 'cs'
                ? 'URL zprávy bude zkopírována do schránky vašeho zařízení.'
                : "The message URL will be copied to your device's clipboard."}
            </li>
          </ol>

          <h3>{language === 'cs' ? 'Mobilní aplikace' : 'Mobile App'}</h3>
          <ol>
            <li>
              {language === 'cs'
                ? 'Přejděte na zprávu, kterou chcete nahlásit.'
                : 'Navigate to the message that you would like to report.'}
            </li>
            <li>
              {language === 'cs'
                ? 'Podržte zprávu pro zobrazení vyskakovacího menu.'
                : 'Hold down on the message to reveal a pull-up menu.'}
            </li>
            <li>
              {language === 'cs' ? 'Vyberte ' : 'Select '}
              <strong>
                {language === 'cs' ? 'Kopírovat odkaz na zprávu' : 'Copy Message Link'}
              </strong>
              {'.'}
            </li>
          </ol>

          <div className={styles.imageContainer}>
            <img
              src="https://cdn.prod.website-files.com/625fe439fb70a9d901e138ab/65cfa5e2bc1788a86dd70aae_r437HtduE3Fplo0xB2V4gk79PgvPVuKg8cVuuwi4RvFrwWKo0ZTbP_TwSSW3DEQ9PNmUe8EfzPHynnl6HQqW2Lwul_mE-HTr4bwrL0rp-WpPruMNNWhCSHx7XwAp9ZSPG8zPCFLi9sMDqm0ykrUk2Lg.png"
              alt={language === 'cs' ? 'Mobilní menu zprávy' : 'Mobile message menu'}
              loading="lazy"
            />
          </div>

          <ol start={4}>
            <li>
              {language === 'cs'
                ? 'URL zprávy bude zkopírována do schránky vašeho zařízení.'
                : "The message URL will be copied to your device's clipboard."}
            </li>
          </ol>
        </section>

        <section id="after-report" className={styles.articleSection}>
          <CopyLinkButton
            sectionId={'after-report'}
            language={language}
            className={styles.copyBtn}
          />
          <h2>
            {language === 'cs'
              ? 'Co se stane po odeslání hlášení?'
              : 'What Happens After I Submit a Report?'}
          </h2>

          <p>
            {language === 'cs'
              ? 'Když odešlete hlášení podle DSA, pošleme vám systémovou soukromou zprávu nebo e-mail, abychom vás informovali, že jsme ho obdrželi. Hlášení jsou zpracovávána na základě priority, jak je popsáno níže, a k jejich zpracování používáme kombinaci automatizovaných a lidských metod kontroly. Můžeme vás také informovat, zda jsou k dispozici bezpečnostní funkce, které by vám mohly pomoci rychleji řešit potenciální škodu.'
              : "When you submit a DSA report, we'll send a system DM or email to let you know that we have received it. Reports are processed based on priority as described below, and we use a combination of automated and human-powered review methods to process reports. We may also let you know if there are safety features available that could help you address the potential harm more quickly."}
          </p>

          <p>
            {language === 'cs'
              ? 'Doba potřebná k přezkoumání hlášení závisí na typu obsahu a závažnosti porušení:'
              : 'The time it takes for us to review a report depends on the type of content and the severity of the violation:'}
          </p>

          <ul>
            <li>
              <strong>
                {language === 'cs' ? 'Hlášení s vysokou závažností' : 'High harm reports'}
              </strong>
              {language === 'cs'
                ? ' (např. bezpečnost dětí, extremismus, bezprostřední škoda): Tato hlášení mají prioritu a Discord se snaží je přezkoumat do 24 až 48 hodin.'
                : ' (e.g., child safety, extremism, imminent harm): These reports are prioritized and Discord aims to review them within 24 to 48 hours.'}
            </li>
            <li>
              <strong>
                {language === 'cs' ? 'Hlášení s nižší závažností' : 'Lower harm reports'}
              </strong>
              {language === 'cs'
                ? ' (např. obtěžování, nenávistné projevy, dezinformace o kyberkriminalitě): Discord se snaží přezkoumat hlášení v této kategorii do týdne.'
                : ' (e.g., harassment, hate speech, cybercrime misinformation): Discord aims to review reports in this category within a week.'}
            </li>
            <li>
              <strong>
                {language === 'cs' ? 'Komplexní vyšetřování' : 'Complex Investigations'}
              </strong>
              {language === 'cs'
                ? ' (např. koordinované zneužívání, vícenásobná porušení nebo odvolání): Hlášení jakéhokoli typu mohou vést k dalšímu přezkoumání nebo vyšetřování ze strany Discordu. Přezkoumání těchto případů může trvat dny až týdny v závislosti na jejich složitosti a potřebě důkladného vyšetřování.'
                : ' (e.g., coordinated abuse, multiple violations, or appeals): Reports of any type might result in additional review or investigation by Discord. These cases may require days to weeks to review, depending on their complexity and the need for thorough investigation.'}
            </li>
          </ul>

          <p>
            {language === 'cs'
              ? 'Časy přezkoumání hlášení se mohou lišit kvůli změnám procesů, nárůstu objemu, technickým problémům a nedořešeným záležitostem.'
              : 'Report review times may fluctuate due to process changes, volume surges, technical issues, and backlogs.'}
          </p>

          <p>
            {language === 'cs'
              ? 'I když ne každé hlášení identifikuje porušení pravidel Discordu, vaše hlášení nám pomáhají zlepšovat a vylepšovat bezpečnostní postupy Discordu. Dáme vám vědět, pokud jsme na základě vašeho hlášení zjistili porušení našich pravidel.'
              : "While not every report identifies a violation of Discord's policies, your reports help us improve and enhance Discord's safety practices. We will let you know if we identified a violation of our policies based on your report."}
          </p>

          <p>
            {language === 'cs'
              ? 'Když odešlete hlášení, vaše identita není sdílena s účtem, který jste nahlásili. To platí i v případě, že se proti hlášení odvolá. Nikdy úmyslně nezveřejňujeme identitu nahlašujícího, pokud to ze zákona není vyžadováno. Příklady, kdy tomu tak může být, jsou situace, kdy Discord obdrží platný právní proces vyžadující poskytnutí identity nahlašujícího a žádosti o odstranění obsahu podle zákona Digital Millennium Copyright Act ("DMCA"). DMCA umožňuje nahlášenému uživateli požádat o další podrobnosti o stížnosti, včetně jména a e-mailové adresy žalobce, popisu údajně porušeného díla a nahlášeného obsahu. V takových případech Discord poskytuje tyto informace v souladu s požadavky autorského zákona.'
              : 'When you submit a report, your identity is not shared with the account you reported. This is true even if the report is appealed. We never intentionally disclose the identity of a reporter unless we are legally required to do so. Examples of when this may be the case are if Discord receives valid legal process requiring us to provide the identity of the reporter and requests to remove content under the Digital Millennium Copyright Act ("DMCA"). The DMCA allows the reported user to request further details about the complaint, including the name and email address of the claimant, a description of the allegedly infringed work, and the reported content. In such cases, Discord provides this information in accordance with copyright law requirements.'}
          </p>

          <p>
            {language === 'cs'
              ? 'Více informací o hlášeních, která obdržíme, a opatřeních, která přijímáme při porušení našich Pokynů komunity nebo Podmínek služby, si můžete přečíst v naší '
              : 'You can read more about the reports we receive and the actions we take on violations of our Community Guidelines or Terms of Service in our '}
            <a href="https://discord.com/safety-transparency">
              {language === 'cs' ? 'Zprávě o transparentnosti' : 'Transparency Report'}
            </a>
            {'.'}
          </p>
        </section>
      </div>

      {/* Tags */}
      <div className={styles.tags}>
        <span className={styles.tagsLabel}>{language === 'cs' ? 'Štítky:' : 'Tags:'}</span>
        <div className={styles.tagsList}>
          <span className={styles.tag}>{language === 'cs' ? 'Nahlašování' : 'Reporting'}</span>
          <span className={styles.tag}>
            {language === 'cs' ? 'Bezpečnost uživatelů' : 'User Safety'}
          </span>
        </div>
      </div>
    </DiscordDocument>
  )
}

export default DiscordReportingAbusiveBehaviorPage
